import "./index.css";

import { SearchOutlined, WarningOutlined } from "@ant-design/icons";
import { Button, Input, Select, Tooltip } from "antd";
import React, { useMemo, useState } from "react";

import { CONSTANTS_WEBSITE } from "../../constants";
import type { StateCapability } from "../../stateCapabilities";
import {
  normalizeStateCapabilities,
  stateCodesFromCapabilities,
} from "../../stateCapabilities";

interface StateCapabilityEditorProps {
  value?: StateCapability[];
  onChange?(value: StateCapability[]): void;
  onStatesChange?(states: string[]): void;
  disabled?: boolean;
}

const STATE_SEARCH_PLACEHOLDER = "Search for a state to add...";

const StateCapabilityEditor: React.FC<StateCapabilityEditorProps> = ({
  value = [],
  onChange,
  onStatesChange,
  disabled,
}) => {
  const capabilities = useMemo(
    () => normalizeStateCapabilities(value, []),
    [value],
  );
  const [bulkQm, setBulkQm] = useState(true);
  const [bulkNonQm, setBulkNonQm] = useState(false);
  const [listFilter, setListFilter] = useState("");

  const stateLabels = useMemo(
    () =>
      new Map(
        CONSTANTS_WEBSITE.STATES.map(({ value, label }) => [
          String(value),
          String(label),
        ]),
      ),
    [],
  );

  const selectedStateCodes = useMemo(
    () => capabilities.map(({ state }) => state),
    [capabilities],
  );

  const filteredCapabilities = useMemo(() => {
    const query = listFilter.trim().toLowerCase();
    if (!query) return capabilities;

    return capabilities.filter(({ state }) => {
      const label = stateLabels.get(state) || state;
      return (
        state.toLowerCase().includes(query) ||
        label.toLowerCase().includes(query)
      );
    });
  }, [capabilities, listFilter, stateLabels]);

  const qmCount = capabilities.filter(({ qm }) => qm).length;
  const nonQmCount = capabilities.filter(({ nonQm }) => nonQm).length;

  function commit(next: StateCapability[]) {
    const normalized = normalizeStateCapabilities(next, []);
    onChange?.(normalized);
    onStatesChange?.(stateCodesFromCapabilities(normalized));
  }

  function updateSelectedStates(states: string[]) {
    const nextSelected = new Set(states.map((state) => String(state)));
    const next = capabilities.filter((item) => nextSelected.has(item.state));
    const existing = new Set(next.map(({ state }) => state));

    states.forEach((state) => {
      const normalizedState = String(state);
      if (!existing.has(normalizedState)) {
        next.push({
          state: normalizedState,
          qm: false,
          nonQm: false,
        });
      }
    });

    commit(next);
  }

  function updateState(
    state: string,
    update: Partial<Pick<StateCapability, "qm" | "nonQm">>,
  ) {
    commit(
      capabilities.map((item) =>
        item.state === state ? { ...item, ...update } : item,
      ),
    );
  }

  function removeState(state: string) {
    commit(capabilities.filter((item) => item.state !== state));
  }

  function applyBulk() {
    if (!bulkQm && !bulkNonQm) return;
    commit(
      capabilities.map((item) => ({
        ...item,
        qm: bulkQm,
        nonQm: bulkNonQm,
      })),
    );
  }

  return (
    <div className="state-capability-editor">
      <p className="state-capability-description">
        For each licensed state, mark whether you originate QM, Non-QM, or
        both. At least one is required per state.
      </p>

      <Select
        mode="multiple"
        showSearch
        allowClear
        value={selectedStateCodes}
        disabled={disabled}
        className="state-capability-add"
        placeholder={STATE_SEARCH_PLACEHOLDER}
        prefix={<SearchOutlined rev={undefined} />}
        maxTagCount={0}
        maxTagPlaceholder={() => STATE_SEARCH_PLACEHOLDER}
        options={CONSTANTS_WEBSITE.STATES}
        optionFilterProp="label"
        onChange={(states) => updateSelectedStates(states.map(String))}
        filterOption={(input, option) =>
          String(option?.label || "")
            .toLowerCase()
            .includes(input.toLowerCase())
        }
      />

      <div className="state-capability-summary">
        <span
          role="status"
          aria-label={`${capabilities.length} state${
            capabilities.length === 1 ? "" : "s"
          }, QM in ${qmCount}, Non-QM in ${nonQmCount}`}
        >
          {capabilities.length} state{capabilities.length === 1 ? "" : "s"}
          <i>·</i> QM in {qmCount} <i>·</i> Non-QM in {nonQmCount}
        </span>
        <div className="state-capability-bulk">
          <small>Apply to all:</small>
          <button
            type="button"
            aria-pressed={bulkQm}
            disabled={disabled}
            className={bulkQm ? "is-qm-active" : ""}
            onClick={() => setBulkQm((current) => !current)}
          >
            QM
          </button>
          <button
            type="button"
            aria-pressed={bulkNonQm}
            disabled={disabled}
            className={bulkNonQm ? "is-non-qm-active" : ""}
            onClick={() => setBulkNonQm((current) => !current)}
          >
            Non-QM
          </button>
          <Button
            type="primary"
            size="small"
            className="state-capability-apply"
            disabled={disabled || !capabilities.length || (!bulkQm && !bulkNonQm)}
            onClick={applyBulk}
          >
            Apply
          </Button>
        </div>
      </div>

      {capabilities.length > 8 && (
        <Input
          allowClear
          disabled={disabled}
          value={listFilter}
          className="state-capability-filter"
          placeholder="Filter states in list..."
          prefix={<SearchOutlined rev={undefined} />}
          onChange={(event) => setListFilter(event.target.value)}
        />
      )}

      <div
        className={`state-capability-list ${
          capabilities.length > 8 ? "is-scrollable" : ""
        }`}
      >
        {filteredCapabilities.map((item) => {
          const invalid = !item.qm && !item.nonQm;
          return (
            <div
              key={item.state}
              className={`state-capability-row ${
                capabilities.length > 8 ? "is-compact" : ""
              } ${invalid ? "is-invalid" : ""}`}
            >
              <div className="state-capability-name">
                <strong>{stateLabels.get(item.state) || item.state}</strong>
                {invalid && (
                  <em>
                    <WarningOutlined rev={undefined} /> Pick at least one
                  </em>
                )}
              </div>

              <div className="state-capability-actions">
                <button
                  type="button"
                  aria-pressed={item.qm}
                  disabled={disabled}
                  className={item.qm ? "is-qm-active" : ""}
                  onClick={() => updateState(item.state, { qm: !item.qm })}
                >
                  QM
                </button>
                <button
                  type="button"
                  aria-pressed={item.nonQm}
                  disabled={disabled}
                  className={item.nonQm ? "is-non-qm-active" : ""}
                  onClick={() =>
                    updateState(item.state, { nonQm: !item.nonQm })
                  }
                >
                  Non-QM
                </button>
                <Tooltip title="Remove state">
                  <button
                    type="button"
                    aria-label={`Remove ${stateLabels.get(item.state) || item.state}`}
                    disabled={disabled}
                    className="state-capability-remove"
                    onClick={() => removeState(item.state)}
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </Tooltip>
              </div>
            </div>
          );
        })}

        {!filteredCapabilities.length && (
          <div className="state-capability-empty">
            {capabilities.length
              ? "No states match your filter."
              : "Search for a state above to get started."}
          </div>
        )}
      </div>
    </div>
  );
};

export default StateCapabilityEditor;
